import type {
  CellarLocation,
  CellarPlaceRecord,
  CreateMovementPayload,
  Movement,
  Wine,
} from "../types";
import { todayInParis } from "./date";
import { getSecureOfflineAdapter } from "./secure-vault";
import { activeCoravins, normalizeLocation } from "./wine";

export type OfflineSyncStatus = "pending" | "sync" | "error";
export type InventoryCacheView = "en_stock" | "epuises";

export const offlineCacheKeys = {
  projection: "projection:version" as const,
  inventory: (view: InventoryCacheView) => `inventory:${view}` as const,
  locations: "locations" as const,
  places: "places" as const,
  // Compatibilité de lecture uniquement. Les versions historiques du coffre
  // dupliquaient chaque fiche complète sous cette clé, en plus de l'inventaire.
  // Toute nouvelle écriture canonique retire ces entrées `wine:*`.
  wine: (id: string) => `wine:${encodeURIComponent(id)}` as const,
};

const LEGACY_WINE_CACHE_PREFIX = "wine:";

function isLegacyWineCacheKey(key: string): boolean {
  return key.startsWith(LEGACY_WINE_CACHE_PREFIX);
}

export type OfflineCacheKey =
  | (typeof offlineCacheKeys)["projection"]
  | ReturnType<(typeof offlineCacheKeys)["inventory"]>
  | (typeof offlineCacheKeys)["locations"]
  | (typeof offlineCacheKeys)["places"]
  | ReturnType<(typeof offlineCacheKeys)["wine"]>;

export interface OfflineCacheEntry<T = unknown> {
  key: string;
  value: T;
  cached_at: number;
}

export interface OfflineReadProjection {
  revision: number;
  updated_at: string;
  wines: Wine[];
  locations: CellarLocation[];
  places: CellarPlaceRecord[];
}

export interface OfflineTemporaryMovement extends Movement {
  emplacement_id: string | null;
  emplacement: string | null;
  lieu_id: string | null;
  lieu: string | null;
  transfert_id: null;
  client_operation_id: string;
  offline_pending: true;
}

export function isOfflineTemporaryMovement(
  movement: Movement,
): movement is OfflineTemporaryMovement {
  const candidate = movement as Partial<OfflineTemporaryMovement>;
  return (
    candidate.offline_pending === true &&
    typeof candidate.client_operation_id === "string" &&
    movement.id === `offline:${candidate.client_operation_id}`
  );
}

export interface OfflineMovementSyncResult {
  movement: Movement | null;
  stock: number;
  stock_emplacement?: number;
  stocks_par_emplacement?: OfflineLocationStock[];
}

export interface OfflineLocationStock {
  emplacement_id: string | null;
  lieu_id: string | null;
  lieu: string | null;
  emplacement: string | null;
  emplacement_nom: string | null;
  stock: number;
}

interface OfflineMovementBase {
  client_operation_id: string;
  wine_id: string;
  sequence: number;
  created_at: string;
  attempts: number;
  status: OfflineSyncStatus;
  error: string | null;
  base_stock: number;
  base_stocks_par_emplacement: OfflineLocationStock[];
  base_stock_emplacement: number;
  previous_stock: number;
  previous_stock_emplacement: number;
  optimistic_stock: number;
  optimistic_stock_emplacement: number;
  temporary_movement: OfflineTemporaryMovement;
  server_result?: OfflineMovementSyncResult;
}

export interface OfflineDrinkBottleOperation extends OfflineMovementBase {
  action: "boire";
  payload: {
    emplacement_id: string | null;
    date_effective: string | null;
    note: string | null;
  };
}

export interface OfflineStockCorrectionOperation extends OfflineMovementBase {
  action: "correction";
  payload: {
    emplacement_id: string | null;
    stock_reel: number;
    stock_attendu: number;
    date_effective: string | null;
    note: string | null;
  };
}

export type OfflineMovementOperation =
  | OfflineDrinkBottleOperation
  | OfflineStockCorrectionOperation;

type OfflineMovementIntent =
  | Pick<OfflineDrinkBottleOperation, "action" | "payload">
  | {
      action: "correction";
      payload: Omit<OfflineStockCorrectionOperation["payload"], "stock_attendu">;
    };

export interface OfflineSnapshot {
  status: OfflineSyncStatus;
  pending_count: number;
  syncing: boolean;
  error: string | null;
}

export interface OfflineFlushResult extends OfflineSnapshot {
  synced_count: number;
}

export type OfflineSubscriber = (snapshot: OfflineSnapshot) => void;
export type OfflineMovementSender = (
  operation: OfflineMovementOperation,
) => Promise<OfflineMovementSyncResult>;

export interface QueueMovementOptions {
  clientOperationId?: string;
  wine?: Wine;
}

export interface QueueMovementResult {
  queued: boolean;
  operation: OfflineMovementOperation;
}

export interface CachedJsonOptions {
  maxAgeMs?: number;
}

export interface OfflinePersistenceAdapter {
  getCache(key: string): Promise<OfflineCacheEntry | null>;
  listCache(): Promise<OfflineCacheEntry[]>;
  putCache(entry: OfflineCacheEntry): Promise<void>;
  commitSnapshot(
    entries: OfflineCacheEntry[],
    mutations: OfflineMovementOperation[],
  ): Promise<boolean>;
  deleteCache(key: string): Promise<void>;
  clearCache(): Promise<void>;
  listMutations(): Promise<OfflineMovementOperation[]>;
  putMutation(operation: OfflineMovementOperation): Promise<void>;
  deleteMutation(clientOperationId: string): Promise<void>;
  getReceipt(clientOperationId: string): Promise<OfflineMovementOperation | null>;
  completeMutation(operation: OfflineMovementOperation): Promise<void>;
  clearMutations(): Promise<void>;
  clearAll(): Promise<void>;
}

export interface OfflinePersistenceOptions {
  adapter?: OfflinePersistenceAdapter;
  now?: () => number;
  createOperationId?: () => string;
}

export interface OfflineFlushOptions {
  /**
   * Limite la passe aux intentions D1 exactes dont l’enveloppe chiffrée a
   * déjà été confirmée par le serveur. Une liste absente conserve le
   * mode manuel historique qui vide toute l’outbox.
   */
  confirmedOperations?: readonly OfflineMovementOperation[];
}

function jsonClone<T>(value: T): T {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError("La valeur hors ligne doit être sérialisable en JSON.");
  }
  return JSON.parse(serialized) as T;
}

function sameCacheValues(
  current: readonly OfflineCacheEntry[],
  replacement: readonly OfflineCacheEntry[],
): boolean {
  if (current.length !== replacement.length) return false;
  const currentByKey = new Map(current.map((entry) => [entry.key, entry]));
  return replacement.every((entry) => {
    const existing = currentByKey.get(entry.key);
    return (
      existing !== undefined &&
      JSON.stringify(existing.value) === JSON.stringify(entry.value)
    );
  });
}

function sameMutationValues(
  current: readonly OfflineMovementOperation[],
  replacement: readonly OfflineMovementOperation[],
): boolean {
  if (current.length !== replacement.length) return false;
  const currentById = new Map(
    current.map((operation) => [operation.client_operation_id, operation]),
  );
  return replacement.every((operation) => {
    const existing = currentById.get(operation.client_operation_id);
    return existing !== undefined && JSON.stringify(existing) === JSON.stringify(operation);
  });
}

function sameRemoteMovementIntent(
  current: OfflineMovementOperation,
  confirmed: OfflineMovementOperation,
): boolean {
  return (
    current.client_operation_id === confirmed.client_operation_id &&
    current.wine_id === confirmed.wine_id &&
    current.action === confirmed.action &&
    JSON.stringify(current.payload) === JSON.stringify(confirmed.payload)
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Synchronisation impossible.";
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeLocationId(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string" || !value.trim()) {
    throw new TypeError("L’emplacement doit être un identifiant valide ou null.");
  }
  return value.trim();
}

function locationKey(locationId: string | null): string {
  return locationId === null ? "__sans_emplacement__" : locationId;
}

function normalizeLocationStocks(value: unknown): OfflineLocationStock[] {
  if (!Array.isArray(value)) return [];
  const rows = new Map<string, OfflineLocationStock>();
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") continue;
    const raw = candidate as Record<string, unknown>;
    const stock = Number(raw.stock);
    if (!Number.isInteger(stock) || stock < 0) {
      throw new TypeError("Chaque stock par emplacement doit être un entier positif ou nul.");
    }
    const emplacementId = normalizeLocationId(raw.emplacement_id);
    const normalized: OfflineLocationStock = {
      emplacement_id: emplacementId,
      lieu_id: optionalString(raw.lieu_id),
      lieu: optionalString(raw.lieu),
      emplacement: optionalString(raw.emplacement),
      emplacement_nom: optionalString(raw.emplacement_nom),
      stock,
    };
    const key = locationKey(emplacementId);
    const existing = rows.get(key);
    rows.set(
      key,
      existing
        ? {
            ...existing,
            ...normalized,
            stock: existing.stock + normalized.stock,
          }
        : normalized,
    );
  }
  return [...rows.values()];
}

function legacyLocationStock(wine: Wine, stock = wine.stock): OfflineLocationStock {
  const legacy = wine as Wine & { emplacement_nom?: string | null };
  return {
    emplacement_id: normalizeLocationId(wine.emplacement_id),
    lieu_id: optionalString(wine.lieu_id),
    lieu: optionalString(wine.lieu),
    emplacement: optionalString(wine.emplacement),
    emplacement_nom:
      optionalString(legacy.emplacement_nom) ?? optionalString(wine.emplacement),
    stock,
  };
}

function locationStocksFromWine(
  wine: Wine,
  fallbackStock = wine.stock,
): OfflineLocationStock[] {
  const raw = (wine as Wine & { stocks_par_emplacement?: unknown })
    .stocks_par_emplacement;
  if (Array.isArray(raw) && (raw.length > 0 || fallbackStock === 0)) {
    return normalizeLocationStocks(raw);
  }
  if (
    fallbackStock === 0 &&
    wine.emplacement_id === null &&
    wine.emplacement === null &&
    wine.lieu_id === null &&
    wine.lieu === null
  ) {
    return [];
  }
  // Les anciennes copies locales ne connaissaient qu'un emplacement sur la fiche.
  return [legacyLocationStock(wine, fallbackStock)];
}

function compatibleWine(wine: Wine): Wine {
  return {
    ...wine,
    stocks_par_emplacement: locationStocksFromWine(wine),
  } as Wine;
}

function stockAtLocation(
  stocks: OfflineLocationStock[],
  locationId: string | null,
): number {
  return (
    stocks.find((entry) => entry.emplacement_id === locationId)?.stock ?? 0
  );
}

function assertCoravinStock(wine: Wine, locationId: string | null, stock: number): void {
  if (stock < activeCoravins(wine, locationId).length) {
    throw new Error(
      "Cet emplacement contient des bouteilles coravinées. Terminez leur suivi dans la fiche avant de les retirer du stock.",
    );
  }
}

function setStockAtLocation(
  stocks: OfflineLocationStock[],
  locationId: string | null,
  stock: number,
  wine?: Wine,
): OfflineLocationStock[] {
  const next = stocks.map((entry) => ({ ...entry }));
  const index = next.findIndex((entry) => entry.emplacement_id === locationId);
  if (index >= 0) {
    next[index] = { ...next[index], stock };
    return next;
  }
  const legacy = wine ? legacyLocationStock(wine, stock) : null;
  next.push(
    legacy && legacy.emplacement_id === locationId
      ? legacy
      : {
          emplacement_id: locationId,
          lieu_id: null,
          lieu: null,
          emplacement: null,
          emplacement_nom: null,
          stock,
        },
  );
  return next;
}

function normalizeSyncResult(result: OfflineMovementSyncResult): OfflineMovementSyncResult {
  if (!Number.isInteger(result?.stock) || result.stock < 0) {
    throw new TypeError("La synchronisation doit renvoyer un stock valide.");
  }
  if (
    result.stock_emplacement !== undefined &&
    (!Number.isInteger(result.stock_emplacement) || result.stock_emplacement < 0)
  ) {
    throw new TypeError(
      "La synchronisation doit renvoyer un stock d’emplacement valide.",
    );
  }
  return jsonClone({
    movement: result.movement ?? null,
    stock: result.stock,
    ...(result.stock_emplacement === undefined
      ? {}
      : { stock_emplacement: result.stock_emplacement }),
    ...(result.stocks_par_emplacement === undefined
      ? {}
      : {
          stocks_par_emplacement: normalizeLocationStocks(
            result.stocks_par_emplacement,
          ),
        }),
  });
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
    value,
  );
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

function defaultOperationId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return fallbackUuid();
}

function sameIntent(
  operation: OfflineMovementOperation,
  wineId: string,
  intent: OfflineMovementIntent,
): boolean {
  if (operation.wine_id !== wineId || operation.action !== intent.action) {
    return false;
  }
  if (operation.action === "boire" && intent.action === "boire") {
    return (
      operation.payload.emplacement_id === intent.payload.emplacement_id &&
      operation.payload.date_effective === intent.payload.date_effective &&
      operation.payload.note === intent.payload.note
    );
  }
  if (operation.action === "correction" && intent.action === "correction") {
    return (
      operation.payload.emplacement_id === intent.payload.emplacement_id &&
      operation.payload.stock_reel === intent.payload.stock_reel &&
      operation.payload.date_effective === intent.payload.date_effective &&
      operation.payload.note === intent.payload.note
    );
  }
  return false;
}

function operationStockDelta(operation: OfflineMovementOperation): number {
  return operation.optimistic_stock - operation.previous_stock;
}

function cloneSnapshot(snapshot: OfflineSnapshot): OfflineSnapshot {
  return { ...snapshot };
}

export class MemoryOfflineAdapter implements OfflinePersistenceAdapter {
  private readonly cache = new Map<string, OfflineCacheEntry>();
  private readonly mutations = new Map<string, OfflineMovementOperation>();
  private readonly receipts = new Map<string, OfflineMovementOperation>();

  async getCache(key: string): Promise<OfflineCacheEntry | null> {
    const value = this.cache.get(key);
    return value ? jsonClone(value) : null;
  }

  async listCache(): Promise<OfflineCacheEntry[]> {
    return [...this.cache.values()].map(jsonClone);
  }

  async putCache(entry: OfflineCacheEntry): Promise<void> {
    this.cache.set(entry.key, jsonClone(entry));
  }

  async commitSnapshot(
    entries: OfflineCacheEntry[],
    mutations: OfflineMovementOperation[],
  ): Promise<boolean> {
    const normalized = entries.map(jsonClone);
    const normalizedMutations = mutations.map(jsonClone);
    if (
      sameCacheValues([...this.cache.values()], normalized) &&
      sameMutationValues([...this.mutations.values()], normalizedMutations)
    ) {
      return false;
    }
    this.cache.clear();
    for (const entry of normalized) this.cache.set(entry.key, entry);
    this.mutations.clear();
    for (const operation of normalizedMutations) {
      this.mutations.set(operation.client_operation_id, operation);
    }
    return true;
  }

  async deleteCache(key: string): Promise<void> {
    this.cache.delete(key);
  }

  async clearCache(): Promise<void> {
    this.cache.clear();
  }

  async listMutations(): Promise<OfflineMovementOperation[]> {
    return [...this.mutations.values()]
      .sort((left, right) => left.sequence - right.sequence)
      .map(jsonClone);
  }

  async putMutation(operation: OfflineMovementOperation): Promise<void> {
    this.mutations.set(operation.client_operation_id, jsonClone(operation));
  }

  async deleteMutation(clientOperationId: string): Promise<void> {
    this.mutations.delete(clientOperationId);
  }

  async getReceipt(
    clientOperationId: string,
  ): Promise<OfflineMovementOperation | null> {
    const value = this.receipts.get(clientOperationId);
    return value ? jsonClone(value) : null;
  }

  async completeMutation(operation: OfflineMovementOperation): Promise<void> {
    const receipt = jsonClone({
      ...operation,
      status: "sync" as const,
      error: null,
    });
    this.receipts.set(operation.client_operation_id, receipt);
    this.mutations.delete(operation.client_operation_id);
  }

  async clearMutations(): Promise<void> {
    this.mutations.clear();
    this.receipts.clear();
  }

  async clearAll(): Promise<void> {
    this.cache.clear();
    this.mutations.clear();
    this.receipts.clear();
  }
}

export class ResilientOfflineAdapter implements OfflinePersistenceAdapter {
  private primaryAvailable = true;

  constructor(
    private readonly primary: OfflinePersistenceAdapter,
    private readonly fallback: OfflinePersistenceAdapter = new MemoryOfflineAdapter(),
  ) {}

  private async read<T>(
    primaryRead: () => Promise<T>,
    fallbackRead: () => Promise<T>,
  ): Promise<T> {
    if (!this.primaryAvailable) return fallbackRead();
    try {
      return await primaryRead();
    } catch {
      this.primaryAvailable = false;
      return fallbackRead();
    }
  }

  private async write(
    primaryWrite: () => Promise<void>,
    fallbackWrite: () => Promise<void>,
  ): Promise<void> {
    await fallbackWrite();
    if (!this.primaryAvailable) return;
    try {
      await primaryWrite();
    } catch {
      this.primaryAvailable = false;
    }
  }

  async getCache(key: string): Promise<OfflineCacheEntry | null> {
    return this.read(
      async () => {
        const entry = await this.primary.getCache(key);
        if (entry) await this.fallback.putCache(entry);
        return entry;
      },
      () => this.fallback.getCache(key),
    );
  }

  async listCache(): Promise<OfflineCacheEntry[]> {
    return this.read(
      () => this.primary.listCache(),
      () => this.fallback.listCache(),
    );
  }

  async putCache(entry: OfflineCacheEntry): Promise<void> {
    await this.write(
      () => this.primary.putCache(entry),
      () => this.fallback.putCache(entry),
    );
  }

  async commitSnapshot(
    entries: OfflineCacheEntry[],
    mutations: OfflineMovementOperation[],
  ): Promise<boolean> {
    const normalized = entries.map(jsonClone);
    const normalizedMutations = mutations.map(jsonClone);
    const fallbackChanged = await this.fallback.commitSnapshot(
      normalized,
      normalizedMutations,
    );
    if (!this.primaryAvailable) return fallbackChanged;
    try {
      return (
        (await this.primary.commitSnapshot(normalized, normalizedMutations)) ||
        fallbackChanged
      );
    } catch {
      this.primaryAvailable = false;
      return fallbackChanged;
    }
  }

  async deleteCache(key: string): Promise<void> {
    await this.write(
      () => this.primary.deleteCache(key),
      () => this.fallback.deleteCache(key),
    );
  }

  async clearCache(): Promise<void> {
    await this.write(
      () => this.primary.clearCache(),
      () => this.fallback.clearCache(),
    );
  }

  async listMutations(): Promise<OfflineMovementOperation[]> {
    return this.read(
      async () => {
        const operations = await this.primary.listMutations();
        for (const operation of operations) {
          await this.fallback.putMutation(operation);
        }
        return operations;
      },
      () => this.fallback.listMutations(),
    );
  }

  async putMutation(operation: OfflineMovementOperation): Promise<void> {
    await this.write(
      () => this.primary.putMutation(operation),
      () => this.fallback.putMutation(operation),
    );
  }

  async deleteMutation(clientOperationId: string): Promise<void> {
    await this.write(
      () => this.primary.deleteMutation(clientOperationId),
      () => this.fallback.deleteMutation(clientOperationId),
    );
  }

  async getReceipt(
    clientOperationId: string,
  ): Promise<OfflineMovementOperation | null> {
    return this.read(
      async () => {
        const receipt = await this.primary.getReceipt(clientOperationId);
        if (receipt) await this.fallback.completeMutation(receipt);
        return receipt;
      },
      () => this.fallback.getReceipt(clientOperationId),
    );
  }

  async completeMutation(operation: OfflineMovementOperation): Promise<void> {
    await this.write(
      () => this.primary.completeMutation(operation),
      () => this.fallback.completeMutation(operation),
    );
  }

  async clearMutations(): Promise<void> {
    await this.write(
      () => this.primary.clearMutations(),
      () => this.fallback.clearMutations(),
    );
  }

  async clearAll(): Promise<void> {
    await this.write(
      () => this.primary.clearAll(),
      () => this.fallback.clearAll(),
    );
  }
}

const CACHE_STORE = "cache";
const MUTATION_STORE = "mutations";
const RECEIPT_STORE = "receipts";

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible."));
  });
}

function idbTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB impossible."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB annulée."));
  });
}

export class IndexedDbOfflineAdapter implements OfflinePersistenceAdapter {
  private readonly database: Promise<IDBDatabase>;

  constructor(
    factory: IDBFactory,
    databaseName = "cave-personnelle-offline",
  ) {
    this.database = new Promise((resolve, reject) => {
      const request = factory.open(databaseName, 1);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(CACHE_STORE)) {
          database.createObjectStore(CACHE_STORE, { keyPath: "key" });
        }
        if (!database.objectStoreNames.contains(MUTATION_STORE)) {
          database.createObjectStore(MUTATION_STORE, {
            keyPath: "client_operation_id",
          });
        }
        if (!database.objectStoreNames.contains(RECEIPT_STORE)) {
          database.createObjectStore(RECEIPT_STORE, {
            keyPath: "client_operation_id",
          });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error("Ouverture IndexedDB impossible."));
      request.onblocked = () => reject(new Error("Mise à jour IndexedDB bloquée."));
    });
  }

  private async transaction(
    stores: string | string[],
    mode: IDBTransactionMode,
  ): Promise<IDBTransaction> {
    return (await this.database).transaction(stores, mode);
  }

  async getCache(key: string): Promise<OfflineCacheEntry | null> {
    const transaction = await this.transaction(CACHE_STORE, "readonly");
    const value = await idbRequest(
      transaction.objectStore(CACHE_STORE).get(key) as IDBRequest<
        OfflineCacheEntry | undefined
      >,
    );
    return value ? jsonClone(value) : null;
  }

  async listCache(): Promise<OfflineCacheEntry[]> {
    const transaction = await this.transaction(CACHE_STORE, "readonly");
    const values = await idbRequest(
      transaction.objectStore(CACHE_STORE).getAll() as IDBRequest<
        OfflineCacheEntry[]
      >,
    );
    return values.map(jsonClone);
  }

  async putCache(entry: OfflineCacheEntry): Promise<void> {
    const transaction = await this.transaction(CACHE_STORE, "readwrite");
    transaction.objectStore(CACHE_STORE).put(jsonClone(entry));
    await idbTransaction(transaction);
  }

  async commitSnapshot(
    entries: OfflineCacheEntry[],
    mutations: OfflineMovementOperation[],
  ): Promise<boolean> {
    const normalized = entries.map(jsonClone);
    const normalizedMutations = mutations.map(jsonClone);
    const transaction = await this.transaction(
      [CACHE_STORE, MUTATION_STORE],
      "readwrite",
    );
    const cacheStore = transaction.objectStore(CACHE_STORE);
    const mutationStore = transaction.objectStore(MUTATION_STORE);
    const [current, currentMutations] = await Promise.all([
      idbRequest(cacheStore.getAll() as IDBRequest<OfflineCacheEntry[]>),
      idbRequest(
        mutationStore.getAll() as IDBRequest<OfflineMovementOperation[]>,
      ),
    ]);
    if (
      sameCacheValues(current, normalized) &&
      sameMutationValues(currentMutations, normalizedMutations)
    ) {
      await idbTransaction(transaction);
      return false;
    }
    cacheStore.clear();
    for (const entry of normalized) cacheStore.put(entry);
    mutationStore.clear();
    for (const operation of normalizedMutations) mutationStore.put(operation);
    await idbTransaction(transaction);
    return true;
  }

  async deleteCache(key: string): Promise<void> {
    const transaction = await this.transaction(CACHE_STORE, "readwrite");
    transaction.objectStore(CACHE_STORE).delete(key);
    await idbTransaction(transaction);
  }

  async clearCache(): Promise<void> {
    const transaction = await this.transaction(CACHE_STORE, "readwrite");
    transaction.objectStore(CACHE_STORE).clear();
    await idbTransaction(transaction);
  }

  async listMutations(): Promise<OfflineMovementOperation[]> {
    const transaction = await this.transaction(MUTATION_STORE, "readonly");
    const values = await idbRequest(
      transaction.objectStore(MUTATION_STORE).getAll() as IDBRequest<
        OfflineMovementOperation[]
      >,
    );
    return values
      .sort((left, right) => left.sequence - right.sequence)
      .map(jsonClone);
  }

  async putMutation(operation: OfflineMovementOperation): Promise<void> {
    const transaction = await this.transaction(MUTATION_STORE, "readwrite");
    transaction.objectStore(MUTATION_STORE).put(jsonClone(operation));
    await idbTransaction(transaction);
  }

  async deleteMutation(clientOperationId: string): Promise<void> {
    const transaction = await this.transaction(MUTATION_STORE, "readwrite");
    transaction.objectStore(MUTATION_STORE).delete(clientOperationId);
    await idbTransaction(transaction);
  }

  async getReceipt(
    clientOperationId: string,
  ): Promise<OfflineMovementOperation | null> {
    const transaction = await this.transaction(RECEIPT_STORE, "readonly");
    const value = await idbRequest(
      transaction.objectStore(RECEIPT_STORE).get(clientOperationId) as IDBRequest<
        OfflineMovementOperation | undefined
      >,
    );
    return value ? jsonClone(value) : null;
  }

  async completeMutation(operation: OfflineMovementOperation): Promise<void> {
    const transaction = await this.transaction(
      [MUTATION_STORE, RECEIPT_STORE],
      "readwrite",
    );
    transaction.objectStore(RECEIPT_STORE).put(
      jsonClone({ ...operation, status: "sync" as const, error: null }),
    );
    transaction
      .objectStore(MUTATION_STORE)
      .delete(operation.client_operation_id);
    await idbTransaction(transaction);
  }

  async clearMutations(): Promise<void> {
    const transaction = await this.transaction(
      [MUTATION_STORE, RECEIPT_STORE],
      "readwrite",
    );
    transaction.objectStore(MUTATION_STORE).clear();
    transaction.objectStore(RECEIPT_STORE).clear();
    await idbTransaction(transaction);
  }

  async clearAll(): Promise<void> {
    const transaction = await this.transaction(
      [CACHE_STORE, MUTATION_STORE, RECEIPT_STORE],
      "readwrite",
    );
    transaction.objectStore(CACHE_STORE).clear();
    transaction.objectStore(MUTATION_STORE).clear();
    transaction.objectStore(RECEIPT_STORE).clear();
    await idbTransaction(transaction);
  }
}

export function createDefaultOfflineAdapter(): OfflinePersistenceAdapter {
  return getSecureOfflineAdapter() ?? new MemoryOfflineAdapter();
}

export class OfflinePersistence {
  private readonly adapter: OfflinePersistenceAdapter;
  private readonly now: () => number;
  private readonly createOperationId: () => string;
  private readonly subscribers = new Set<OfflineSubscriber>();
  private readyPromise: Promise<void>;
  private hydrationEpoch = 0;
  private queue: OfflineMovementOperation[] = [];
  private flushPromise: Promise<OfflineFlushResult> | null = null;
  private flushRequestedWhileRunning = false;
  private cacheProjectionPromise: Promise<void> = Promise.resolve();
  private snapshot: OfflineSnapshot = {
    status: "sync",
    pending_count: 0,
    syncing: false,
    error: null,
  };

  constructor(options: OfflinePersistenceOptions = {}) {
    this.adapter = options.adapter ?? createDefaultOfflineAdapter();
    this.now = options.now ?? Date.now;
    this.createOperationId = options.createOperationId ?? defaultOperationId;
    this.readyPromise = this.hydrate(this.hydrationEpoch);
  }

  /**
   * L'union des deux vues d'inventaire est la projection canonique du coffre.
   * Une entrée `wine:*` n'est consultée qu'en dernier recours pour ouvrir puis
   * migrer sans perte une ancienne enveloppe.
   */
  private winesFromCacheEntries(entries: readonly OfflineCacheEntry[]): Wine[] {
    const winesById = new Map<string, Wine>();
    let completeCanonicalInventory = true;

    for (const view of ["en_stock", "epuises"] as const) {
      const entry = entries.find(
        (candidate) => candidate.key === offlineCacheKeys.inventory(view),
      );
      if (!Array.isArray(entry?.value)) {
        completeCanonicalInventory = false;
        continue;
      }
      for (const rawWine of entry.value as Wine[]) {
        const wine = compatibleWine(jsonClone(rawWine));
        if (!wine.id || winesById.has(wine.id)) continue;
        winesById.set(wine.id, wine);
      }
    }

    // Deux listes présentes, même vides, décrivent tout l'inventaire. Une
    // ancienne clé de détail absente de cette union correspond alors à une
    // fiche supprimée et ne doit surtout pas la ressusciter.
    if (completeCanonicalInventory) return [...winesById.values()];

    // Les anciens coffres peuvent ne contenir qu'un détail `wine:*`. Ils
    // restent lisibles, mais ne remplacent jamais une fiche déjà présente dans
    // l'inventaire canonique, même si les deux copies historiques divergent.
    for (const entry of entries) {
      if (
        !isLegacyWineCacheKey(entry.key) ||
        entry.value === null ||
        typeof entry.value !== "object" ||
        Array.isArray(entry.value)
      ) {
        continue;
      }
      const wine = compatibleWine(jsonClone(entry.value as Wine));
      if (!wine.id || winesById.has(wine.id)) continue;
      winesById.set(wine.id, wine);
    }

    return [...winesById.values()];
  }

  /**
   * Remplace toutes les représentations d'une fiche par une seule occurrence,
   * dans la vue correspondant à son stock. Les autres caches (version, lieux,
   * emplacements) sont conservés tels quels.
   */
  private canonicalCacheEntries(
    currentEntries: readonly OfflineCacheEntry[],
    wines: readonly Wine[],
    cachedAt: number,
  ): OfflineCacheEntry[] {
    const cacheByKey = new Map(
      currentEntries
        .filter(
          (entry) =>
            !isLegacyWineCacheKey(entry.key) &&
            entry.key !== offlineCacheKeys.inventory("en_stock") &&
            entry.key !== offlineCacheKeys.inventory("epuises"),
        )
        .map((entry) => [entry.key, jsonClone(entry)]),
    );
    const uniqueWines = new Map<string, Wine>();
    for (const rawWine of wines) {
      const wine = compatibleWine(jsonClone(rawWine));
      if (!wine.id) {
        throw new TypeError("Une fiche sans identifiant ne peut pas être mise en cache.");
      }
      if (uniqueWines.has(wine.id)) {
        throw new TypeError("Une fiche ne peut apparaître qu'une fois dans le coffre.");
      }
      uniqueWines.set(wine.id, wine);
    }
    const canonicalWines = [...uniqueWines.values()];
    cacheByKey.set(offlineCacheKeys.inventory("en_stock"), {
      key: offlineCacheKeys.inventory("en_stock"),
      value: canonicalWines.filter((wine) => wine.stock > 0),
      cached_at: cachedAt,
    });
    cacheByKey.set(offlineCacheKeys.inventory("epuises"), {
      key: offlineCacheKeys.inventory("epuises"),
      value: canonicalWines.filter((wine) => wine.stock === 0),
      cached_at: cachedAt,
    });
    return [...cacheByKey.values()];
  }

  private async cachedWineSourceWithoutHydration(
    id: string,
    allowLegacyWithCompleteInventory = false,
  ): Promise<Wine | null> {
    let completeCanonicalInventory = true;
    for (const view of ["en_stock", "epuises"] as const) {
      const entry = await this.adapter.getCache(offlineCacheKeys.inventory(view));
      if (!Array.isArray(entry?.value)) {
        completeCanonicalInventory = false;
        continue;
      }
      const wine = (entry.value as Wine[]).find((candidate) => candidate.id === id);
      if (wine) return wine;
    }
    if (completeCanonicalInventory && !allowLegacyWithCompleteInventory) {
      return null;
    }
    const legacyDetail = await this.adapter.getCache(offlineCacheKeys.wine(id));
    if (legacyDetail?.value && typeof legacyDetail.value === "object") {
      return legacyDetail.value as Wine;
    }
    return null;
  }

  private async makeOperationCompatible(
    operation: OfflineMovementOperation,
  ): Promise<void> {
    const source = await this.cachedWineSourceWithoutHydration(
      operation.wine_id,
      true,
    );
    const rawPayload = operation.payload as OfflineMovementOperation["payload"] & {
      emplacement_id?: unknown;
    };
    const hasPersistedLocation = Object.prototype.hasOwnProperty.call(
      rawPayload,
      "emplacement_id",
    );
    const emplacementId = hasPersistedLocation
      ? normalizeLocationId(rawPayload.emplacement_id)
      : normalizeLocationId(source?.emplacement_id);
    rawPayload.emplacement_id = emplacementId;

    const persistedBaseStocks = (
      operation as OfflineMovementOperation & {
        base_stocks_par_emplacement?: unknown;
      }
    ).base_stocks_par_emplacement;
    const baseStocks = Array.isArray(persistedBaseStocks)
      ? normalizeLocationStocks(persistedBaseStocks)
      : source
        ? locationStocksFromWine(source, operation.base_stock)
        : setStockAtLocation([], emplacementId, operation.base_stock);
    operation.base_stocks_par_emplacement = baseStocks;

    const baseLocationStock = stockAtLocation(baseStocks, emplacementId);
    if (!Number.isInteger(operation.base_stock_emplacement)) {
      operation.base_stock_emplacement = baseLocationStock;
    }
    if (!Number.isInteger(operation.previous_stock_emplacement)) {
      operation.previous_stock_emplacement =
        baseLocationStock + (operation.previous_stock - operation.base_stock);
    }
    if (!Number.isInteger(operation.optimistic_stock_emplacement)) {
      operation.optimistic_stock_emplacement =
        operation.previous_stock_emplacement +
        (operation.optimistic_stock - operation.previous_stock);
    }
    const location = setStockAtLocation(
      baseStocks,
      emplacementId,
      operation.optimistic_stock_emplacement,
      source ?? undefined,
    ).find((entry) => entry.emplacement_id === emplacementId);
    operation.temporary_movement = this.buildTemporaryMovement(
      operation,
      operationStockDelta(operation),
      location,
    );
  }

  private async hydrate(epoch: number): Promise<void> {
    try {
      const queue = await this.adapter.listMutations();
      if (epoch !== this.hydrationEpoch) return;
      for (const operation of queue) {
        await this.makeOperationCompatible(operation);
        if (epoch !== this.hydrationEpoch) return;
        if (operation.status === "sync") {
          operation.status = "pending";
          operation.error = null;
        }
        await this.adapter.putMutation(operation);
        if (epoch !== this.hydrationEpoch) return;
      }
      queue.sort((left, right) => left.sequence - right.sequence);
      if (epoch !== this.hydrationEpoch) return;
      this.queue = queue;
      this.refreshSnapshot();
    } catch (error) {
      if (epoch !== this.hydrationEpoch) return;
      throw error;
    }
  }

  async ready(): Promise<void> {
    await this.readyPromise;
  }

  private async reloadFromStorageWithoutProjectionWait(): Promise<void> {
    const epoch = this.hydrationEpoch + 1;
    this.hydrationEpoch = epoch;
    this.queue = [];
    this.refreshSnapshot(false);
    this.readyPromise = this.hydrate(epoch);
    await this.readyPromise;
  }

  async reloadFromStorage(): Promise<void> {
    if (this.flushPromise) await this.flushPromise;
    await this.ready();
    await this.serializeCacheProjection(() =>
      this.reloadFromStorageWithoutProjectionWait(),
    );
  }

  /**
   * Publie une réplique externe et recharge l’outbox sous la même barrière
   * que queue/cancel. Une action commencée avant le remplacement termine donc
   * avant lui, et une action suivante relit obligatoirement le nouveau cache.
   */
  async replaceStoredSnapshot(replacement: () => Promise<void>): Promise<void> {
    if (this.flushPromise) await this.flushPromise;
    await this.ready();
    await this.serializeCacheProjection(async () => {
      await replacement();
      await this.reloadFromStorageWithoutProjectionWait();
    });
  }

  resetDecryptedState(): void {
    const epoch = this.hydrationEpoch + 1;
    this.hydrationEpoch = epoch;
    this.queue = [];
    this.refreshSnapshot(false);
    // Appelé après le verrouillage de l'adapter : l'hydratation attendra le
    // prochain déverrouillage et ne conservera aucune outbox en clair.
    this.readyPromise = this.hydrate(epoch);
  }

  private notify(): void {
    const snapshot = cloneSnapshot(this.snapshot);
    for (const subscriber of this.subscribers) {
      try {
        subscriber(snapshot);
      } catch {
        // Un observateur ne doit jamais interrompre une écriture locale.
      }
    }
  }

  private setSnapshot(next: OfflineSnapshot): void {
    const changed =
      next.status !== this.snapshot.status ||
      next.pending_count !== this.snapshot.pending_count ||
      next.syncing !== this.snapshot.syncing ||
      next.error !== this.snapshot.error;
    this.snapshot = next;
    if (changed) this.notify();
  }

  private refreshSnapshot(syncing = false): void {
    const failed = this.queue.find((operation) => operation.status === "error");
    this.setSnapshot({
      status: syncing ? "sync" : failed ? "error" : this.queue.length ? "pending" : "sync",
      pending_count: this.queue.length,
      syncing,
      error: failed?.error ?? null,
    });
  }

  subscribe(subscriber: OfflineSubscriber): () => void {
    this.subscribers.add(subscriber);
    void this.readyPromise.then(() => {
      if (this.subscribers.has(subscriber)) subscriber(cloneSnapshot(this.snapshot));
    });
    return () => this.subscribers.delete(subscriber);
  }

  async getSnapshot(): Promise<OfflineSnapshot> {
    await this.ready();
    return cloneSnapshot(this.snapshot);
  }

  private async writeCachedJson<T>(key: OfflineCacheKey, value: T): Promise<void> {
    const clonedValue = jsonClone(value);
    const existing = await this.adapter.getCache(key);
    if (
      existing &&
      JSON.stringify(existing.value) === JSON.stringify(clonedValue)
    ) {
      return;
    }
    await this.adapter.putCache({
      key,
      value: clonedValue,
      cached_at: this.now(),
    });
  }

  private async readCachedJsonWithoutProjectionWait<T>(
    key: OfflineCacheKey,
    options: CachedJsonOptions = {},
  ): Promise<T | null> {
    const entry = await this.adapter.getCache(key);
    if (!entry) return null;
    if (
      options.maxAgeMs !== undefined &&
      this.now() - entry.cached_at > options.maxAgeMs
    ) {
      await this.adapter.deleteCache(key);
      return null;
    }
    return jsonClone(entry.value) as T;
  }

  async cacheJson<T>(key: OfflineCacheKey, value: T): Promise<void> {
    if (isLegacyWineCacheKey(key)) {
      if (
        value === null ||
        typeof value !== "object" ||
        Array.isArray(value) ||
        typeof (value as unknown as { id?: unknown }).id !== "string" ||
        offlineCacheKeys.wine((value as unknown as { id: string }).id) !== key
      ) {
        throw new TypeError("La fiche ne correspond pas à sa clé de cache.");
      }
      await this.cacheWine(value as unknown as Wine);
      return;
    }
    await this.writeCachedJson(key, value);
  }

  async getCachedJson<T>(
    key: OfflineCacheKey,
    options: CachedJsonOptions = {},
  ): Promise<T | null> {
    await this.cacheProjectionPromise;
    return this.readCachedJsonWithoutProjectionWait<T>(key, options);
  }

  private projectWineInMemory(
    sourceValue: Wine,
    operations: OfflineMovementOperation[],
    baseStock: number,
    baseLocationStocksValue: OfflineLocationStock[],
  ): Wine {
    const source = compatibleWine(jsonClone(sourceValue));
    const baseLocationStocks = normalizeLocationStocks(baseLocationStocksValue);
    let stock = baseStock;
    let locationStocks = normalizeLocationStocks(baseLocationStocks);
    if (!locationStocks.length && baseStock > 0) {
      locationStocks = locationStocksFromWine(source, baseStock);
    }
    for (const operation of operations) {
      const previousStock = stock;
      const emplacementId = normalizeLocationId(operation.payload.emplacement_id);
      const previousLocationStock = stockAtLocation(
        locationStocks,
        emplacementId,
      );
      if (operation.action === "boire" && previousLocationStock < 1) {
        throw new Error(
          "Aucune bouteille disponible à retirer dans cet emplacement.",
        );
      }
      const optimisticLocationStock =
        operation.action === "boire"
          ? previousLocationStock - 1
          : operation.payload.stock_reel;
      assertCoravinStock(source, emplacementId, optimisticLocationStock);
      stock += optimisticLocationStock - previousLocationStock;
      locationStocks = setStockAtLocation(
        locationStocks,
        emplacementId,
        optimisticLocationStock,
        source,
      );
      operation.base_stock = baseStock;
      operation.base_stocks_par_emplacement = jsonClone(baseLocationStocks);
      operation.base_stock_emplacement = stockAtLocation(
        baseLocationStocks,
        emplacementId,
      );
      operation.previous_stock = previousStock;
      operation.previous_stock_emplacement = previousLocationStock;
      operation.optimistic_stock = stock;
      operation.optimistic_stock_emplacement = optimisticLocationStock;
      if (operation.action === "correction") {
        operation.payload.stock_attendu = previousLocationStock;
      }
      operation.temporary_movement = this.buildTemporaryMovement(
        operation,
        optimisticLocationStock - previousLocationStock,
        locationStocks.find((entry) => entry.emplacement_id === emplacementId),
      );
    }

    const serverMovements = (source.mouvements ?? []).filter(
      (movement) =>
        !isOfflineTemporaryMovement(movement) &&
        !movement.id.startsWith("offline:"),
    );
    return jsonClone({
      ...source,
      stock,
      stocks_par_emplacement: locationStocks,
      mouvements: [
        ...serverMovements,
        ...operations.map((operation) => operation.temporary_movement),
      ],
    });
  }

  private async projectQueueInMemory(
    authoritativeWines: Wine[],
  ): Promise<{
    wines: Wine[];
    mutations: OfflineMovementOperation[];
  }> {
    const winesById = new Map(
      authoritativeWines.map((wine) => [wine.id, compatibleWine(jsonClone(wine))]),
    );
    const mutations = jsonClone(this.queue).sort(
      (left, right) => left.sequence - right.sequence,
    );
    const wineIds = [...new Set(mutations.map((operation) => operation.wine_id))];

    for (const wineId of wineIds) {
      const operations = mutations.filter(
        (operation) => operation.wine_id === wineId,
      );
      let source = winesById.get(wineId);
      if (!source) {
        const cached = await this.cachedWineSourceWithoutHydration(wineId, true);
        if (!cached) {
          throw new Error(
            "Une action en attente référence une fiche absente de la projection.",
          );
        }
        // Une fiche supprimée ailleurs reste visible tant que son action locale
        // n’a pas été acceptée ou rejetée : l’outbox ne devient jamais orpheline.
        source = compatibleWine(jsonClone(cached));
      }

      const alreadyProjected = (source.mouvements ?? []).some(
        isOfflineTemporaryMovement,
      );
      const baseStock = alreadyProjected
        ? operations[0].base_stock
        : source.stock;
      const baseLocationStocks = alreadyProjected
        ? operations[0].base_stocks_par_emplacement
        : locationStocksFromWine(source, source.stock);
      winesById.set(
        wineId,
        this.projectWineInMemory(
          source,
          operations,
          baseStock,
          baseLocationStocks,
        ),
      );
    }

    return { wines: [...winesById.values()], mutations };
  }

  async replaceProjection(projection: OfflineReadProjection): Promise<boolean> {
    await this.ready();
    if (this.flushPromise) await this.flushPromise;
    if (
      !Number.isSafeInteger(projection.revision) ||
      projection.revision < 0 ||
      typeof projection.updated_at !== "string" ||
      projection.updated_at.trim() === ""
    ) {
      throw new TypeError("La version de projection locale est invalide.");
    }

    const winesById = new Map<string, Wine>();
    for (const wine of projection.wines) {
      const normalized = compatibleWine(jsonClone(wine));
      if (!normalized.id || winesById.has(normalized.id)) {
        throw new TypeError("La projection contient une fiche vin invalide ou dupliquée.");
      }
      winesById.set(normalized.id, normalized);
    }
    const authoritativeWines = [...winesById.values()];
    const locations = projection.locations.map((location) =>
      normalizeLocation(jsonClone(location) as unknown as Record<string, unknown>),
    );
    const places = jsonClone(projection.places);

    return this.serializeCacheProjection(async () => {
      const currentVersion = await this.readCachedJsonWithoutProjectionWait<{
        revision: number;
        updated_at: string;
      }>(offlineCacheKeys.projection);
      if (
        currentVersion &&
        Number.isSafeInteger(currentVersion.revision) &&
        currentVersion.revision > projection.revision
      ) {
        return false;
      }

      const projected = await this.projectQueueInMemory(authoritativeWines);
      const cachedAt = this.now();
      const entries: OfflineCacheEntry[] = [
        {
          key: offlineCacheKeys.projection,
          value: {
            revision: projection.revision,
            updated_at: projection.updated_at,
          },
          cached_at: cachedAt,
        },
        {
          key: offlineCacheKeys.inventory("en_stock"),
          value: projected.wines.filter((wine) => wine.stock > 0),
          cached_at: cachedAt,
        },
        {
          key: offlineCacheKeys.inventory("epuises"),
          value: projected.wines.filter((wine) => wine.stock === 0),
          cached_at: cachedAt,
        },
        { key: offlineCacheKeys.locations, value: locations, cached_at: cachedAt },
        { key: offlineCacheKeys.places, value: places, cached_at: cachedAt },
      ];
      const replaced = await this.adapter.commitSnapshot(
        entries,
        projected.mutations,
      );
      this.queue = projected.mutations;
      this.refreshSnapshot();
      return replaced;
    });
  }

  async cacheInventory(view: InventoryCacheView, wines: Wine[]): Promise<void> {
    await this.ready();
    if (this.flushPromise) await this.flushPromise;
    await this.serializeCacheProjection(async () => {
      const cacheEntries = await this.adapter.listCache();
      const incoming = wines.map((wine) => compatibleWine(jsonClone(wine)));
      const incomingIds = new Set(incoming.map((wine) => wine.id));
      const retained = this.winesFromCacheEntries(cacheEntries).filter(
        (wine) =>
          !incomingIds.has(wine.id) &&
          (view === "en_stock" ? wine.stock === 0 : wine.stock > 0),
      );
      const projected = await this.projectQueueInMemory([...retained, ...incoming]);
      const replaced = this.canonicalCacheEntries(
        cacheEntries,
        projected.wines,
        this.now(),
      );
      await this.adapter.commitSnapshot(replaced, projected.mutations);
      this.queue = projected.mutations;
      this.refreshSnapshot();
    });
  }

  async getCachedInventory(
    view: InventoryCacheView,
    options?: CachedJsonOptions,
  ): Promise<Wine[] | null> {
    await this.cacheProjectionPromise;
    const wines = await this.readCachedJsonWithoutProjectionWait<Wine[]>(
      offlineCacheKeys.inventory(view),
      options,
    );
    return wines?.map(compatibleWine) ?? null;
  }

  async cacheLocations(locations: CellarLocation[]): Promise<void> {
    await this.writeCachedJson(offlineCacheKeys.locations, locations);
  }

  async getCachedLocations(
    options?: CachedJsonOptions,
  ): Promise<CellarLocation[] | null> {
    await this.cacheProjectionPromise;
    const locations = await this.readCachedJsonWithoutProjectionWait<
      Record<string, unknown>[]
    >(
      offlineCacheKeys.locations,
      options,
    );
    return locations?.map(normalizeLocation) ?? null;
  }

  async cachePlaces(places: CellarPlaceRecord[]): Promise<void> {
    await this.writeCachedJson(offlineCacheKeys.places, places);
  }

  async getCachedPlaces(
    options?: CachedJsonOptions,
  ): Promise<CellarPlaceRecord[] | null> {
    await this.cacheProjectionPromise;
    return this.readCachedJsonWithoutProjectionWait<CellarPlaceRecord[]>(
      offlineCacheKeys.places,
      options,
    );
  }

  async cacheWine(wine: Wine): Promise<void> {
    await this.ready();
    if (this.flushPromise) await this.flushPromise;
    await this.serializeCacheProjection(async () => {
      const cacheEntries = await this.adapter.listCache();
      const normalized = compatibleWine(jsonClone(wine));
      const winesById = new Map(
        this.winesFromCacheEntries(cacheEntries).map((candidate) => [
          candidate.id,
          candidate,
        ]),
      );
      winesById.set(normalized.id, normalized);
      const projected = await this.projectQueueInMemory([...winesById.values()]);
      const replaced = this.canonicalCacheEntries(
        cacheEntries,
        projected.wines,
        this.now(),
      );
      await this.adapter.commitSnapshot(replaced, projected.mutations);
      this.queue = projected.mutations;
      this.refreshSnapshot();
    });
  }

  async getCachedWine(
    id: string,
    options?: CachedJsonOptions,
  ): Promise<Wine | null> {
    await this.cacheProjectionPromise;
    let completeCanonicalInventory = true;
    for (const view of ["en_stock", "epuises"] as const) {
      const key = offlineCacheKeys.inventory(view);
      const entry = await this.adapter.getCache(key);
      if (!entry || !Array.isArray(entry.value)) {
        completeCanonicalInventory = false;
        continue;
      }
      if (
        options?.maxAgeMs !== undefined &&
        this.now() - entry.cached_at > options.maxAgeMs
      ) {
        await this.adapter.deleteCache(key);
        completeCanonicalInventory = false;
        continue;
      }
      const canonical = (entry.value as Wine[]).find((wine) => wine.id === id);
      if (canonical) return compatibleWine(canonical);
    }
    if (completeCanonicalInventory) return null;
    const wine = await this.readCachedJsonWithoutProjectionWait<Wine>(
      offlineCacheKeys.wine(id),
      options,
    );
    return wine ? compatibleWine(wine) : null;
  }

  async deleteCachedJson(key: OfflineCacheKey): Promise<void> {
    await this.adapter.deleteCache(key);
  }

  async purgeCache(): Promise<void> {
    await this.adapter.clearCache();
  }

  private async serializeCacheProjection<T>(work: () => Promise<T>): Promise<T> {
    const next = this.cacheProjectionPromise.then(work, work);
    this.cacheProjectionPromise = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  private buildTemporaryMovement(
    operation: Omit<OfflineMovementOperation, "temporary_movement">,
    quantity: number,
    location?: OfflineLocationStock,
  ): OfflineTemporaryMovement {
    return {
      id: `offline:${operation.client_operation_id}`,
      vin_id: operation.wine_id,
      emplacement_id: operation.payload.emplacement_id,
      emplacement: location?.emplacement ?? null,
      lieu_id: location?.lieu_id ?? null,
      lieu: location?.lieu ?? null,
      transfert_id: null,
      quantite: quantity,
      motif: operation.action === "boire" ? "Bouteille bue" : "Correction",
      date_effective: operation.payload.date_effective,
      date_creation: operation.created_at,
      prix_unitaire: null,
      vendeur: null,
      note: operation.payload.note,
      client_operation_id: operation.client_operation_id,
      offline_pending: true,
    };
  }

  private async rebuildOptimisticWine(
    wineId: string,
    baseStock: number,
    baseLocationStocks: OfflineLocationStock[],
    operations: OfflineMovementOperation[],
    fallback?: Wine,
    confirmedMovement?: Movement | null,
  ): Promise<void> {
    const cacheEntries = await this.adapter.listCache();
    const winesById = new Map(
      this.winesFromCacheEntries(cacheEntries).map((wine) => [wine.id, wine]),
    );
    const source = winesById.get(wineId) ?? fallback;
    if (!source) return;
    const persistedMutations = new Map(
      (await this.adapter.listMutations()).map((operation) => [
        operation.client_operation_id,
        operation,
      ]),
    );

    let stock = baseStock;
    let locationStocks = normalizeLocationStocks(baseLocationStocks);
    if (!locationStocks.length && baseStock > 0) {
      locationStocks = locationStocksFromWine(source, baseStock);
    }
    for (const operation of operations) {
      const previousStock = stock;
      const emplacementId = normalizeLocationId(operation.payload.emplacement_id);
      const previousLocationStock = stockAtLocation(
        locationStocks,
        emplacementId,
      );
      if (operation.action === "boire" && previousLocationStock < 1) {
        throw new Error(
          "Aucune bouteille disponible à retirer dans cet emplacement.",
        );
      }
      const optimisticLocationStock =
        operation.action === "boire"
          ? previousLocationStock - 1
          : operation.payload.stock_reel;
      assertCoravinStock(source, emplacementId, optimisticLocationStock);
      stock += optimisticLocationStock - previousLocationStock;
      locationStocks = setStockAtLocation(
        locationStocks,
        emplacementId,
        optimisticLocationStock,
        source,
      );
      operation.base_stock = baseStock;
      operation.base_stocks_par_emplacement = jsonClone(baseLocationStocks);
      operation.base_stock_emplacement = stockAtLocation(
        baseLocationStocks,
        emplacementId,
      );
      operation.previous_stock = previousStock;
      operation.previous_stock_emplacement = previousLocationStock;
      operation.optimistic_stock = stock;
      operation.optimistic_stock_emplacement = optimisticLocationStock;
      if (operation.action === "correction") {
        operation.payload.stock_attendu = previousLocationStock;
      }
      operation.temporary_movement = this.buildTemporaryMovement(
        operation,
        optimisticLocationStock - previousLocationStock,
        locationStocks.find(
          (entry) => entry.emplacement_id === emplacementId,
        ),
      );
      persistedMutations.set(
        operation.client_operation_id,
        jsonClone(operation),
      );
    }

    const serverMovements = (source.mouvements ?? []).filter(
      (movement) =>
        !isOfflineTemporaryMovement(movement) &&
        !movement.id.startsWith("offline:"),
    );
    if (
      confirmedMovement &&
      !serverMovements.some((movement) => movement.id === confirmedMovement.id)
    ) {
      serverMovements.push(confirmedMovement);
    }
    const updated: Wine = jsonClone({
      ...source,
      stock,
      stocks_par_emplacement: locationStocks,
      mouvements: [
        ...serverMovements,
        ...operations.map((operation) => operation.temporary_movement),
      ],
    });
    winesById.set(updated.id, updated);
    const replacement = this.canonicalCacheEntries(
      cacheEntries,
      [...winesById.values()],
      this.now(),
    );
    await this.adapter.commitSnapshot(
      replacement,
      [...persistedMutations.values()].sort(
        (left, right) => left.sequence - right.sequence,
      ),
    );
  }

  private async commitOptimisticWineState(
    wineId: string,
    nextQueueValue: OfflineMovementOperation[],
    baseStock: number,
    baseLocationStocks: OfflineLocationStock[],
    fallback?: Wine,
  ): Promise<OfflineMovementOperation[]> {
    const cacheEntries = await this.adapter.listCache();
    const winesById = new Map(
      this.winesFromCacheEntries(cacheEntries).map((wine) => [wine.id, wine]),
    );
    const source = winesById.get(wineId) ?? fallback;
    if (!source) {
      throw new Error(
        "La fiche doit être mise en cache avant une modification hors ligne.",
      );
    }

    const nextQueue = jsonClone(nextQueueValue).sort(
      (left, right) => left.sequence - right.sequence,
    );
    const wineOperations = nextQueue.filter(
      (operation) => operation.wine_id === wineId,
    );
    const updated = this.projectWineInMemory(
      source,
      wineOperations,
      baseStock,
      baseLocationStocks,
    );
    winesById.set(updated.id, updated);
    await this.adapter.commitSnapshot(
      this.canonicalCacheEntries(
        cacheEntries,
        [...winesById.values()],
        this.now(),
      ),
      nextQueue,
    );
    return nextQueue;
  }

  private normalizeMovementIntent(
    payload: CreateMovementPayload,
    fallbackLocationId: string | null,
  ): OfflineMovementIntent {
    const rawPayload = payload as CreateMovementPayload & {
      emplacement_id?: unknown;
    };
    const emplacementId = Object.prototype.hasOwnProperty.call(
      rawPayload,
      "emplacement_id",
    )
      ? normalizeLocationId(rawPayload.emplacement_id)
      : normalizeLocationId(fallbackLocationId);
    if (payload.motif === "Achat" || payload.motif === "Stock initial") {
      throw new TypeError("Les achats restent disponibles uniquement en ligne.");
    }
    if (payload.motif === "Bouteille bue") {
      const currentDate = todayInParis(new Date(this.now()));
      const effectiveDate = payload.date_effective || currentDate;
      if (effectiveDate > currentDate) {
        throw new TypeError("La date de consommation ne peut pas être dans le futur.");
      }
      return {
        action: "boire",
        payload: {
          emplacement_id: emplacementId,
          date_effective: effectiveDate,
          note: payload.note?.trim() || null,
        },
      };
    }
    if (
      payload.stock_reel === undefined ||
      !Number.isInteger(payload.stock_reel) ||
      payload.stock_reel < 0
    ) {
      throw new TypeError("Le stock réel doit être un entier positif ou nul.");
    }
    return {
      action: "correction",
      payload: {
        emplacement_id: emplacementId,
        stock_reel: payload.stock_reel,
        date_effective: payload.date_effective || null,
        note: payload.note?.trim() || null,
      },
    };
  }

  async queueMovement(
    wineId: string,
    payload: CreateMovementPayload,
    options: QueueMovementOptions = {},
  ): Promise<QueueMovementResult> {
    if (this.flushPromise) this.flushRequestedWhileRunning = true;
    await this.ready();
    return this.serializeCacheProjection(async () => {
    if (!this.flushPromise) {
      this.queue = await this.adapter.listMutations();
      this.queue.sort((left, right) => left.sequence - right.sequence);
    }
    const normalizedWineId = wineId.trim();
    if (!normalizedWineId) throw new TypeError("La fiche vin est obligatoire.");

    const cachedSource = await this.cachedWineSourceWithoutHydration(
      normalizedWineId,
    );
    const source = cachedSource
      ? compatibleWine(cachedSource)
      : options.wine
        ? compatibleWine(jsonClone(options.wine))
        : null;
    if (!source) {
      throw new Error(
        "La fiche doit être mise en cache avant une modification hors ligne.",
      );
    }
    const intent = this.normalizeMovementIntent(
      payload,
      normalizeLocationId(source.emplacement_id),
    );
    const clientOperationId =
      options.clientOperationId?.trim() || this.createOperationId();
    if (!isUuid(clientOperationId)) {
      throw new TypeError("client_operation_id doit être un UUID valide.");
    }

    const existing = this.queue.find(
      (operation) => operation.client_operation_id === clientOperationId,
    );
    const receipt = existing
      ? null
      : await this.adapter.getReceipt(clientOperationId);
    const duplicate = existing ?? receipt;
    if (duplicate) {
      const duplicatePayload = duplicate.payload as typeof duplicate.payload & {
        emplacement_id?: unknown;
      };
      if (
        !Object.prototype.hasOwnProperty.call(
          duplicatePayload,
          "emplacement_id",
        )
      ) {
        duplicatePayload.emplacement_id = normalizeLocationId(
          source.emplacement_id,
        );
      }
      if (!sameIntent(duplicate, normalizedWineId, intent)) {
        throw new Error(
          "Ce client_operation_id appartient déjà à une autre opération.",
        );
      }
      return { queued: false, operation: jsonClone(duplicate) };
    }

    const wineOperations = this.queue.filter(
      (operation) => operation.wine_id === normalizedWineId,
    );
    const baseStock = wineOperations[0]?.base_stock ?? source.stock;
    const baseLocationStocks = wineOperations[0]?.base_stocks_par_emplacement ??
      locationStocksFromWine(source, baseStock);
    let currentStock = baseStock;
    let currentLocationStocks = normalizeLocationStocks(baseLocationStocks);
    for (const operation of wineOperations) {
      currentStock += operationStockDelta(operation);
      currentLocationStocks = setStockAtLocation(
        currentLocationStocks,
        operation.payload.emplacement_id,
        operation.optimistic_stock_emplacement,
        source,
      );
    }
    const currentLocationStock = stockAtLocation(
      currentLocationStocks,
      intent.payload.emplacement_id,
    );
    if (intent.action === "boire" && currentLocationStock < 1) {
      throw new Error(
        "Aucune bouteille disponible à retirer dans cet emplacement.",
      );
    }

    const createdAt = new Date(this.now()).toISOString();
    const sequence =
      this.queue.reduce(
        (maximum, operation) => Math.max(maximum, operation.sequence),
        0,
      ) + 1;
    const optimisticLocationStock =
      intent.action === "boire"
        ? currentLocationStock - 1
        : intent.payload.stock_reel;
    assertCoravinStock(source, intent.payload.emplacement_id, optimisticLocationStock);
    const optimisticStock =
      currentStock + optimisticLocationStock - currentLocationStock;
    const operationIntent =
      intent.action === "correction"
        ? {
            action: intent.action,
            payload: {
              ...intent.payload,
              stock_attendu: currentLocationStock,
            },
          }
        : intent;
    const base = {
      client_operation_id: clientOperationId,
      wine_id: normalizedWineId,
      sequence,
      created_at: createdAt,
      attempts: 0,
      status: "pending" as const,
      error: null,
      base_stock: baseStock,
      base_stocks_par_emplacement: jsonClone(baseLocationStocks),
      base_stock_emplacement: stockAtLocation(
        baseLocationStocks,
        intent.payload.emplacement_id,
      ),
      previous_stock: currentStock,
      previous_stock_emplacement: currentLocationStock,
      optimistic_stock: optimisticStock,
      optimistic_stock_emplacement: optimisticLocationStock,
    };
    const withoutTemporary = {
      ...base,
      ...operationIntent,
    } as Omit<OfflineMovementOperation, "temporary_movement">;
    const operation = {
      ...withoutTemporary,
      temporary_movement: this.buildTemporaryMovement(
        withoutTemporary,
        optimisticLocationStock - currentLocationStock,
        setStockAtLocation(
          currentLocationStocks,
          intent.payload.emplacement_id,
          optimisticLocationStock,
          source,
        ).find(
          (entry) => entry.emplacement_id === intent.payload.emplacement_id,
        ),
      ),
    } as OfflineMovementOperation;

    // Outbox et cache optimiste deviennent durables dans la même transaction.
    const nextQueue = await this.commitOptimisticWineState(
      normalizedWineId,
      [...this.queue, operation],
      baseStock,
      baseLocationStocks,
      source,
    );
    this.queue = nextQueue;
    this.refreshSnapshot();
    return { queued: true, operation: jsonClone(operation) };
    });
  }

  async cancelQueuedMovement(clientOperationId: string): Promise<boolean> {
    await this.ready();
    return this.serializeCacheProjection(async () => {
    const index = this.queue.findIndex(
      (operation) => operation.client_operation_id === clientOperationId,
    );
    if (index === -1 || this.queue[index].status === "sync") return false;

    const operation = this.queue[index];
    const sameWineBefore = this.queue.filter(
      (candidate) => candidate.wine_id === operation.wine_id,
    );
    const baseStock = sameWineBefore[0]?.base_stock ?? operation.base_stock;
    const cachedFallback = await this.cachedWineSourceWithoutHydration(
      operation.wine_id,
      true,
    );
    const fallback = cachedFallback ? compatibleWine(cachedFallback) : null;
    const baseLocationStocks =
      sameWineBefore[0]?.base_stocks_par_emplacement ??
      operation.base_stocks_par_emplacement ??
      (fallback ? locationStocksFromWine(fallback, baseStock) : []);

    const nextQueue = await this.commitOptimisticWineState(
      operation.wine_id,
      this.queue.filter(
        (candidate) => candidate.client_operation_id !== clientOperationId,
      ),
      baseStock,
      baseLocationStocks,
      fallback ?? undefined,
    );
    this.queue = nextQueue;
    this.refreshSnapshot();
    return true;
    });
  }

  async listQueuedMovements(): Promise<OfflineMovementOperation[]> {
    await this.ready();
    await this.cacheProjectionPromise;
    return jsonClone(this.queue);
  }

  private async reconcileCompletedOperation(
    operation: OfflineMovementOperation,
    result: OfflineMovementSyncResult,
  ): Promise<void> {
    const remaining = this.queue.filter(
      (candidate) => candidate.wine_id === operation.wine_id,
    );
    const cachedFallback = await this.cachedWineSourceWithoutHydration(
      operation.wine_id,
      true,
    );
    const fallback = cachedFallback ? compatibleWine(cachedFallback) : null;
    let authoritativeLocationStocks =
      result.stocks_par_emplacement === undefined
        ? normalizeLocationStocks(operation.base_stocks_par_emplacement)
        : normalizeLocationStocks(result.stocks_par_emplacement);
    if (result.stocks_par_emplacement === undefined) {
      const emplacementId = operation.payload.emplacement_id;
      const otherLocationsStock = authoritativeLocationStocks.reduce(
        (sum, entry) =>
          entry.emplacement_id === emplacementId ? sum : sum + entry.stock,
        0,
      );
      const inferredLocationStock =
        result.stock_emplacement ?? Math.max(0, result.stock - otherLocationsStock);
      authoritativeLocationStocks = setStockAtLocation(
        authoritativeLocationStocks,
        emplacementId,
        inferredLocationStock,
        fallback ?? undefined,
      );
    }
    await this.rebuildOptimisticWine(
      operation.wine_id,
      result.stock,
      authoritativeLocationStocks,
      remaining,
      fallback ?? undefined,
      result.movement,
    );
  }

  private async runFlush(
    sender: OfflineMovementSender,
    confirmedOperations: ReadonlyMap<string, OfflineMovementOperation> | null,
  ): Promise<OfflineFlushResult> {
    await this.ready();
    await this.serializeCacheProjection(async () => {
      this.queue = await this.adapter.listMutations();
      this.queue.sort((left, right) => left.sequence - right.sequence);
      this.refreshSnapshot(false);
    });
    let syncedCount = 0;

    while (this.queue.length > 0) {
      const preparation = await this.serializeCacheProjection(async () => {
        const operation = this.queue[0];
        if (!operation) return { state: "empty" as const };
        const confirmed = confirmedOperations?.get(
          operation.client_operation_id,
        );
        if (
          confirmedOperations &&
          (!confirmed || !sameRemoteMovementIntent(operation, confirmed))
        ) {
          // L’outbox est causale : ne jamais sauter une action non confirmée
          // ou dont le rebase a modifié la requête après le CAS du coffre.
          return { state: "empty" as const };
        }
        const existingReceipt = await this.adapter.getReceipt(
          operation.client_operation_id,
        );
        if (existingReceipt) {
          this.queue = this.queue.filter(
            (candidate) =>
              candidate.client_operation_id !== operation.client_operation_id,
          );
          const result =
            existingReceipt.server_result ??
            ({
              movement: null,
              stock: operation.optimistic_stock,
              stock_emplacement: operation.optimistic_stock_emplacement,
            } satisfies OfflineMovementSyncResult);
          await this.reconcileCompletedOperation(operation, result);
          await this.adapter.deleteMutation(operation.client_operation_id);
          syncedCount += 1;
          this.refreshSnapshot(this.queue.length > 0);
          return { state: "receipt" as const };
        }

        operation.status = "sync";
        operation.error = null;
        operation.attempts += 1;
        await this.adapter.putMutation(operation);
        this.refreshSnapshot(true);
        return {
          state: "send" as const,
          operation: jsonClone(operation),
        };
      });
      if (preparation.state === "receipt") continue;
      if (preparation.state === "empty") break;
      const sentOperation = preparation.operation;

      try {
        const result = normalizeSyncResult(await sender(sentOperation));
        await this.serializeCacheProjection(async () => {
          const operation = this.queue.find(
            (candidate) =>
              candidate.client_operation_id ===
              sentOperation.client_operation_id,
          );
          if (!operation) {
            throw new Error("L’action synchronisée a disparu de l’outbox locale.");
          }
          operation.server_result = result;
          await this.adapter.putMutation(operation);
          this.queue = this.queue.filter(
            (candidate) =>
              candidate.client_operation_id !== operation.client_operation_id,
          );
          // Cache, reçu idempotent et retrait d’outbox restent invisibles aux
          // lecteurs jusqu’à la fin de cette section sérialisée.
          await this.reconcileCompletedOperation(operation, result);
          await this.adapter.completeMutation(operation);
          syncedCount += 1;
          this.refreshSnapshot(this.queue.length > 0);
        });
      } catch (error) {
        await this.serializeCacheProjection(async () => {
          let operation = this.queue.find(
            (candidate) =>
              candidate.client_operation_id ===
              sentOperation.client_operation_id,
          );
          if (!operation) {
            operation = jsonClone(sentOperation);
            this.queue.unshift(operation);
            this.queue.sort((left, right) => left.sequence - right.sequence);
          }
          operation.status = "error";
          operation.error = errorMessage(error);
          try {
            await this.adapter.putMutation(operation);
          } catch {
            // La copie en mémoire conserve au moins l’erreur jusqu’au prochain essai.
          }
          this.refreshSnapshot(false);
        });
        break;
      }
    }

    await this.serializeCacheProjection(async () => {
      this.refreshSnapshot(false);
    });
    return { ...cloneSnapshot(this.snapshot), synced_count: syncedCount };
  }

  async syncQueuedMovements(
    sender: OfflineMovementSender,
    options: OfflineFlushOptions = {},
  ): Promise<OfflineFlushResult> {
    if (this.flushPromise) {
      this.flushRequestedWhileRunning = true;
      return this.flushPromise;
    }
    const confirmedOperations = options.confirmedOperations
      ? new Map(
          options.confirmedOperations.map((operation) => [
            operation.client_operation_id,
            jsonClone(operation),
          ]),
        )
      : null;
    const promise = (async () => {
      let result: OfflineFlushResult | null = null;
      let syncedCount = 0;
      do {
        this.flushRequestedWhileRunning = false;
        result = await this.runFlush(sender, confirmedOperations);
        syncedCount += result.synced_count;
      } while (this.flushRequestedWhileRunning);
      return { ...result, synced_count: syncedCount };
    })();
    this.flushPromise = promise;
    try {
      return await promise;
    } finally {
      this.flushPromise = null;
    }
  }

  async purge(): Promise<void> {
    await this.ready();
    if (this.flushPromise) await this.flushPromise;
    await this.adapter.clearAll();
    this.queue = [];
    this.refreshSnapshot(false);
  }
}

export function createOfflinePersistence(
  options: OfflinePersistenceOptions = {},
): OfflinePersistence {
  return new OfflinePersistence(options);
}

export const offlinePersistence = createOfflinePersistence();
